#import "./fragments/personFilledPositionsFragment.graphql"

fragment MembershipFragment on Membership {
  currentCompositeId
  personId
  positionId
  groupId
  startDate
  endDate
  ownerRole
  attributes
  fte
  type
}

fragment MembershipGroupFragment on Group {
  id
  name
  type
  parentIds
  hierarchyIds
  hierarchy
  attributes {
    attributeType
    value
  }
  permissions {
    canMutateMemberships
  }
}

fragment MembershipPersonFragment on Person {
  aggregateId
  fte
  surname
  firstName
  preferredName
  avatar
  jobTitle
  disabled
}

fragment MembershipMutationResponseFragment on MembershipMutationResponse {
  person {
    aggregateId
    memberships(input: $searchInput) {
      ...MembershipFragment
      group {
        ...MembershipGroupFragment
      }
    }
  }
}

query getMemberships(
  $input: SearchMembershipsQuery!
  $includePerson: Boolean = false
  $includeGroup: Boolean = true
  $personMembershipsInput: SearchMembershipsQuery!
  $includePersonMemberships: Boolean = false
  $includePersonMembershipGroup: Boolean = false
  $includePositionFields: Boolean = false
) {
  result: searchMemberships(input: $input) {
    memberships {
      ...MembershipFragment
      position @include(if: $includePositionFields) {
        id
        occupancyStatus
        establishedFte
        filledFte
        person {
          aggregateId
          preferredName
          firstName
          surname
          avatar
          fte
        }
        incumbents {
          aggregateId
          preferredName
          firstName
          surname
          avatar
          fte
          jobTitle
        }
      }
      group @include(if: $includeGroup) {
        ...MembershipGroupFragment
      }
      person @include(if: $includePerson) {
        ...MembershipPersonFragment
        isVacancy
        # Positions this person currently fills — drives the clickable
        # "Position: <name>" card badge on timeline member rows.
        ...PersonFilledPositionsFragment @include(if: $includePositionFields)
        allocations {
          fte
          targetGroupId
        }
        memberships(input: $personMembershipsInput)
          @include(if: $includePersonMemberships) {
          ...MembershipFragment
          group @include(if: $includePersonMembershipGroup) {
            id
            name
            type
          }
        }
        memberOf {
          id
          parentIds
          name
          type
          hierarchy
          hierarchyIds
        }
      }
    }
  }
}

query getPersonMemberships(
  $aggregateId: String!
  $input: SearchMembershipsQuery!
  $includePositionFields: Boolean = false
) {
  person: getPerson(aggregateId: $aggregateId, returnWithLegacyProps: false) {
    aggregateId
    fte
    isVacancy
    vacancyId
    permissions {
      canMutateMemberships
    }
    memberships(input: $input) {
      ...MembershipFragment
      position @include(if: $includePositionFields) {
        id
        person {
          aggregateId
          preferredName
          firstName
          surname
          fte
        }
      }
      group {
        ...MembershipGroupFragment
      }
    }
  }
}

mutation addMembership(
  $input: AddMembershipInput!
  $searchInput: SearchMembershipsQuery!
) {
  results: addMembership(input: $input) {
    ...MembershipMutationResponseFragment
  }
}

mutation addMembershipOnly($input: AddMembershipInput!) {
  results: addMembership(input: $input) {
    person {
      aggregateId
    }
  }
}

mutation removeMembership(
  $input: RemoveMembershipInput!
  $searchInput: SearchMembershipsQuery!
) {
  results: removeMembership(input: $input) {
    ...MembershipMutationResponseFragment
  }
}

mutation updateMembership(
  $input: UpdateMembershipInput!
  $searchInput: SearchMembershipsQuery!
) {
  results: updateMembership(input: $input) {
    ...MembershipMutationResponseFragment
  }
}

mutation mutateMemberships(
  $input: MutateMembershipsInput!
  $searchInput: SearchMembershipsQuery!
) {
  results: mutateMemberships(input: $input) {
    people {
      aggregateId
      memberships(input: $searchInput) {
        ...MembershipFragment
        group {
          ...MembershipGroupFragment
        }
      }
    }
    groups {
      id
    }
  }
}

mutation fillPositionVacancy(
  $input: FillPositionVacancyInput!
  $searchInput: SearchMembershipsQuery!
) {
  results: fillPositionVacancy(input: $input) {
    isTriggered
    triggerRejectReasons
    person {
      aggregateId
      vacancyId
      memberships(input: $searchInput) {
        ...MembershipFragment
        group {
          ...MembershipGroupFragment
        }
      }
    }
    position {
      id
      person {
        aggregateId
        isVacancy
        memberships(input: $searchInput) {
          ...MembershipFragment
          group {
            ...MembershipGroupFragment
          }
        }
      }
    }
  }
}

mutation vacatePosition(
  $input: VacatePositionInput!
  $searchInput: SearchMembershipsQuery!
) {
  results: vacatePosition(input: $input) {
    person {
      aggregateId
      vacancyId
      memberships(input: $searchInput) {
        ...MembershipFragment
        group {
          ...MembershipGroupFragment
        }
      }
    }
    position {
      id
      person {
        aggregateId
        isVacancy
        memberships(input: $searchInput) {
          ...MembershipFragment
          group {
            ...MembershipGroupFragment
          }
        }
      }
    }
  }
}

query getGroupCapacityAndAllocatedFte(
  $input: GroupCapacityAndAllocatedFteInput!
) {
  result: getGroupCapacityAndAllocatedFte(input: $input) {
    dateRangeList {
      date
      capacityFte
      allocatedFte
    }
  }
}
